import {
    FetchState,
    SftpVendorTypeOption,
    SftpMapping,
    SftpVendorResponse,
} from "@/shared/types";
import * as FileContributionService from "@/shared/api/fileContributionService";
import {
    FileProcessorSettingsState,
    FileUploadProcessingRule,
    FileUploadErrorProcessingRule
} from "@/employer/types";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import Vue from "vue";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: FileProcessorSettingsState = {
    fetchState: FetchState.Uninitialized,
    fileProcessorRule: FileUploadProcessingRule.override,
    errorHandlingRule: FileUploadErrorProcessingRule.forceEntireSuccess,
    sftpVendorTypeFetchState: FetchState.Uninitialized,
    sftpVendorType: {},
    sftpMappingFetchState: FetchState.Uninitialized,
    sftpMappings: {}
};

export const useFileProcessorSettingsStore = defineStore("fileProcessorSettings", {
    state: (): FileProcessorSettingsState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        fileUploadProcessingRule: state => state.fileProcessorRule,
        fileUploadErrorProcessingRule: state => state.errorHandlingRule,
        sftpVendorTypeLoaded: state => state.sftpVendorTypeFetchState === FetchState.Loaded,
        getSftpVendorType: state => (employerId: number) => state.sftpVendorType[employerId],
        sftpMappingsLoaded: state => state.sftpMappingFetchState === FetchState.Loaded,
        getSftpMappings: state => (employerId: number) => state.sftpMappings[employerId]
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setProcessingRules (rules: {
            fileProcessorRule: FileUploadProcessingRule,
            errorHandlingRule: FileUploadErrorProcessingRule
        }) {
            this.fileProcessorRule = rules.fileProcessorRule;
            this.errorHandlingRule = rules.errorHandlingRule;
        },
        setSftpVendorTypeFetchState (fetchState: FetchState) {
            this.sftpVendorTypeFetchState = fetchState;
        },
        setSftpMappingFetchState (fetchState: FetchState) {
            this.sftpMappingFetchState = fetchState;
        },
        setSftpSettingsResponse (
            { employerId, response }: { employerId: number, response: SftpVendorResponse }) {
            Vue.set(this.sftpVendorType, employerId, response.sftpVendorType);
            Vue.set(this.sftpMappings, employerId, response.sftpMappings);
        },
        removeSftpMapping ({ employerId, sftpMapId }: { employerId: number, sftpMapId: number }) {
            const filteredSftpMappings = _.filter(this.sftpMappings[employerId], x => x.sftpMapId !== sftpMapId);

            Vue.set(this.sftpMappings, employerId, filteredSftpMappings);
        },
        addSftpMapping ({ employerId, sftpMapping }: { employerId: number, sftpMapping: SftpMapping }) {
            const mappings = this.sftpMappings[employerId] = [ ...this.sftpMappings[employerId], sftpMapping ];
            Vue.set(this.sftpMappings, employerId, mappings);
        },
        getFileProcessorSettings (employerId: number) {
            return new Promise((resolve, reject) => {
                this.setFetchState(FetchState.Loading);
                return FileContributionService.getFileProcessorSettings(employerId).then(resp => {
                    this.setProcessingRules(resp.data);
                    this.setFetchState(FetchState.Loaded);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        saveFileProcessorSettings (rules: {
            fileProcessorRule: FileUploadProcessingRule,
            errorHandlingRule: FileUploadErrorProcessingRule,
            employerId: number
        }) {
            return new Promise<void>((resolve, reject) => {
                if (this.fetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setFetchState(FetchState.Loading);
                    return FileContributionService.saveFileProcessorSettings(rules.employerId, rules)
                        .then(resp => {
                            this.setFetchState(FetchState.Loaded);

                            const result = resp.data;
                            if (result.errorMessage) {
                                storeHelper.handleError({
                                    error: result.errorMessage, rethrow: false
                                });
                            } else {
                                const notifierStore = useNotifierStore();
                                notifierStore.setSuccess(`The file processor settings have been updated.`);
                            }
                        }).catch(err => {
                            return storeHelper.handleError({ error: err, rethrow: true });
                        });
                }
            });
        },
        loadEmployerSftpVendorType (employerId) {
            if (this.sftpVendorType[employerId]) {
                return Promise.resolve();
            }

            if (this.sftpVendorTypeFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setSftpVendorTypeFetchState(FetchState.Loading);

            return FileContributionService.getSftpVendorType(employerId)
                .then(payload => this.setSftpSettingsResponse({ employerId, response: payload.data }))
                .catch(error => storeHelper.handleError({ error, rethrow: false }))
                .finally(() => this.setSftpVendorTypeFetchState(FetchState.Loaded));
        },
        updateEmployerSftpVendorType (
            { employerId, sftpVendorType }: {
                employerId: number, sftpVendorType: SftpVendorTypeOption
            }) {
            if (this.sftpVendorTypeFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setSftpVendorTypeFetchState(FetchState.Loading);

            return FileContributionService.putSftpVendorType(employerId, sftpVendorType)
                .then(payload => this.setSftpSettingsResponse({ employerId, response: payload.data.value }))
                .catch(error => storeHelper.handleError({ error, rethrow: true }))
                .finally(() => this.setSftpVendorTypeFetchState(FetchState.Loaded));
        },
        beginAddSftpMapping ({ employerId, sftpMapCode }: { employerId: number, sftpMapCode: string }) {
            if (this.sftpVendorTypeFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setSftpMappingFetchState(FetchState.Loading);

            return FileContributionService.addSftpMapping(employerId, sftpMapCode)
                .then(payload => {
                    this.addSftpMapping({ employerId, sftpMapping: payload.data });
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }))
                .finally(() => this.setSftpMappingFetchState(FetchState.Loaded));
        },
        deleteSftpMapping (
            { employerId, sftpMapId }: { employerId: number, sftpMapId: number }) {
            if (this.sftpVendorTypeFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setSftpMappingFetchState(FetchState.Loading);

            return FileContributionService.deleteSftpMapping(employerId, sftpMapId)
                .then(payload => this.removeSftpMapping({ employerId, sftpMapId }))
                .catch(error => storeHelper.handleError({ error, rethrow: true }))
                .finally(() => this.setSftpMappingFetchState(FetchState.Loaded));
        },
    }
});